import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";

export function useYearsReportData(yearsInput) {
  const years = useMemo(() => {
    const uniq = Array.from(
      new Set((yearsInput || []).map((y) => Number(y)).filter((y) => Number.isFinite(y)))
    );
    uniq.sort((a, b) => a - b);
    return uniq;
  }, [yearsInput]);

  const [byYear, setByYear] = useState(() => ({}));
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    const missing = years.filter((y) => !byYear[y]);
    if (!missing.length) return undefined;

    const fetchMissing = async () => {
      setLoading(true);
      setErr("");
      try {
        const results = await Promise.all(
          missing.map(async (year) => {
            const [txnSnap, eventSnap] = await Promise.all([
              getDocs(query(collection(db, "transactions"), where("Year", "==", year))),
              getDocs(query(collection(db, "privateEvents"), where("year", "==", year))),
            ]);

            const transactions = txnSnap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .sort(
                (a, b) =>
                  new Date(`${a.Date} ${a.Time}`) - new Date(`${b.Date} ${b.Time}`)
              );
            const privateEvents = eventSnap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

            return [year, { transactions, privateEvents }];
          })
        );

        if (cancelled) return;
        setByYear((prev) => {
          const next = { ...prev };
          for (const [year, data] of results) next[year] = data;
          return next;
        });
      } catch (e) {
        console.error("Error fetching year report data:", e);
        if (!cancelled) setErr("Failed to load comparison data. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMissing();
    return () => {
      cancelled = true;
    };
  }, [years, byYear]);

  return { byYear, loading, err };
}

